import axios, { AxiosInstance } from 'axios';
import { REACT_APP_BACKEND_URL } from '../utils/constants';
import { SESSION_EXPIRED_EVENT } from '../utils/session';

/** The token a request was sent with, from its Authorization header. */
const sentToken = (config: any): string | null => {
  const header = config?.headers?.Authorization ?? config?.headers?.authorization;
  return typeof header === 'string' && header.startsWith('Bearer ') ? header.slice(7) : null;
};

/**
 * Whether a 401 for this URL means the backend refused the session. The
 * mirror proxy passes on the status codes of a custom network's mirror node,
 * so its 401s say nothing about the session.
 */
const refusesSession = (url: string | undefined): boolean =>
  !!url && url.startsWith(`${REACT_APP_BACKEND_URL}/`) && !url.includes('/mirror-proxy/');

let instance: AxiosInstance | null = null;

/** One client for the backend, sending whichever session token is stored when the request goes out. */
const backendClient = (): AxiosInstance => {
  if (instance) return instance;
  instance = axios.create();
  instance.interceptors.request.use(config => {
    const token = localStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });
  instance.interceptors.response.use(undefined, error => {
    const token = sentToken(error?.config);
    // Drop the refused session and say so, unless a newer one replaced it meanwhile
    if (
      error?.response?.status === 401 &&
      refusesSession(error?.config?.url) &&
      token &&
      localStorage.getItem('token') === token
    ) {
      localStorage.removeItem('token');
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT, { detail: { token } }));
    }
    return Promise.reject(error);
  });
  return instance;
};

/** The backend client while a session is stored, otherwise nothing. */
export const getAuthAxiosInstance = (): AxiosInstance | void => {
  if (localStorage.getItem('token')) return backendClient();
};
